import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useMediaQuery } from "react-responsive";
import { useMapboxKey } from "@/components/maps/useMapboxKey";
import { addNamedPins, addStateLines, removeNamedPins, removePolyline, setMaxBounds } from "@/utils/map";
import { PickupPin, PredictionDeliveryPin } from "@/components/common/svgs";
import { ErrorComponent } from "@/components/common/errors";
import debounce from "lodash/debounce";
import { useStopsPolyline } from "@/hooks/useStopsPolyline";

export const LoadboardMap = ({ id, stops, isOpen }) => {
    const mapRef = useRef(null);
    const mapContainer = useRef(null);
    const isDesktop = useMediaQuery({
        query: "(min-width: 1025px)",
    });
    const mapboxAccessKey = useMapboxKey(isOpen);
    const [mapIsLoaded, setMapIsLoaded] = useState(false);
    const [styleIsLoaded, setStyleIsLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [redraw, setRedraw] = useState(0);

    const routeLine = useStopsPolyline(isOpen ? stops : []);

    const addLocationPins = () => {
        // stopCoordinates is nullable in the API — skip stops without them
        const stopsWithCoordinates = stops.filter((stop) => stop.stopCoordinates);
        if (mapRef?.current && stopsWithCoordinates.length > 1) {
            const map = mapRef.current;
            const startPoint = [stopsWithCoordinates[0].stopCoordinates.longitude, stopsWithCoordinates[0].stopCoordinates.latitude];
            const lastStop = stopsWithCoordinates[stopsWithCoordinates.length - 1];
            const endPoint = [lastStop.stopCoordinates.longitude, lastStop.stopCoordinates.latitude];

            const bounds = new mapboxgl.LngLatBounds(startPoint, endPoint);
            const padding = { top: 50, bottom: 30, left: 30, right: 30 };
            setMaxBounds(map);

            stopsWithCoordinates.forEach((stop, index) => {
                const point = [stop.stopCoordinates.longitude, stop.stopCoordinates.latitude];
                bounds.extend(point);
                addNamedPins(id, map, index === stopsWithCoordinates.length - 1 ? PredictionDeliveryPin : PickupPin, point, -17);
            });
            map.fitBounds(bounds, { padding, duration: 1000 });
        }
    };

    const addPolyLine = () => {
        if (mapRef?.current && stops?.length && routeLine.length) {
            const map = mapRef.current;
            try {
                map.addSource("routeLine", {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: [
                            {
                                type: "Feature",
                                geometry: {
                                    type: "LineString",
                                    coordinates: routeLine,
                                },
                            },
                        ],
                    },
                });
                map.addLayer({
                    id: "routeLine",
                    type: "line",
                    source: "routeLine",
                    paint: {
                        "line-width": 2,
                        "line-color": "#43BC98",
                    },
                });
            } catch (err) {
                console.log("Error adding polyline", err);
            }
        }
    };

    const debouncedResize = debounce(() => {
        setRedraw(Math.random());
    }, 500);

    useEffect(() => {
        if (mapContainer.current && mapboxAccessKey && isOpen) {
            mapboxgl.accessToken = mapboxAccessKey;
            const currentMap = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/lioneightdev/cm4zecvtw009w01sr1ytv71xi",
                center: [-98.5795, 37.8283],
                zoom: isDesktop ? 2.6 : 2.15,
                maxZoom: 8,
                minZoom: isDesktop ? 2.6 : 2.15,
                dragRotate: false,
                touchZoomRotate: false,
                pitchWithRotate: false,
                fitBoundsOptions: { duration: 400 },
            });
            mapRef.current = currentMap;

            currentMap.on("load", async () => {
                await addStateLines(currentMap);
                setMapIsLoaded(true);
            });

            currentMap.on("style.load", async () => {
                setStyleIsLoaded(true);
            });

            currentMap.on("error", async () => {
                setError(true);
            });

            currentMap.on("resize", () => {
                debouncedResize();
            });

            return () => {
                removeNamedPins(id);
                currentMap.remove();
            };
        }
    }, [mapboxAccessKey]);

    useEffect(() => {
        if (mapIsLoaded && styleIsLoaded && isOpen) {
            const map = mapRef.current;
            if (map && stops?.length) {
                removeNamedPins(id);
                removePolyline(map);
                addLocationPins();
                setTimeout(() => {
                    addPolyLine();
                }, 300);
            }
        }
    }, [mapRef?.current, mapIsLoaded, styleIsLoaded, redraw, stops, routeLine]);

    return (
        <div className="w-[calc(50%+16px)] h-[289px] flex relative overflow-hidden rounded-sm z-0">
            {error ? (
                <ErrorComponent className={`bg-cm-p-surface h-[289px]`} />
            ) : (
                <div
                    id={id}
                    ref={mapContainer}
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        width: "100%",
                        maxHeight: "289px",
                    }}
                />
            )}
        </div>
    );
};
